// client/src/services/gameNotificationService.js

import notificationService from './notificationService';
import soundManager from '../utils/soundManager';

/**
 * Service spécialisé pour les notifications relatives au jeu
 * Ce service ajoute des effets sonores et des durées spécifiques 
 * selon le type d'événement de jeu
 */
class GameNotificationService {
  constructor(notificationService) {
    this.notificationService = notificationService;
  }

  /**
   * Notifier que c'est le tour du joueur
   */
  yourTurn() {
    soundManager.play('notification');
    this.notificationService.info('C\'est votre tour!', 4500);
  }

  /**
   * Notifier une attaque
   * @param {string} attackerName - Nom de la carte attaquante
   * @param {string} targetName - Nom de la carte ciblée
   * @param {number} damage - Dégâts infligés
   */
  attack(attackerName, targetName, damage) {
    soundManager.play('attack');
    this.notificationService.warning(
      `${attackerName} attaque ${targetName || 'directement'} et inflige ${damage} points de dégâts!`,
      6500
    );
  }

  /**
   * Notifier des dégâts reçus
   * @param {number} damage - Montant des dégâts
   */
  damage(damage) {
    soundManager.play('damage');
    this.notificationService.error(
      `Vous avez subi ${damage} points de dégâts!`,
      5500
    );
  }

  /**
   * Notifier l'invocation d'une carte (monstre)
   * @param {string} cardName - Nom de la carte invoquée
   * @param {boolean} byOpponent - true si c'est l'adversaire qui invoque
   */
  summon(cardName, byOpponent = false) {
    soundManager.play('cardPlace');
    this.notificationService.info(
      byOpponent
        ? `L'adversaire invoque ${cardName}!`
        : `${cardName} a été invoqué!`,
      5500
    );
  }

  /**
   * Notifier le lancement d'un sort
   * @param {string} cardName - Nom du sort lancé
   * @param {boolean} byOpponent - true si c'est l'adversaire qui le lance
   */
  spell(cardName, byOpponent = false) {
    soundManager.play('cardFlip');
    this.notificationService.info(
      byOpponent
        ? `L'adversaire lance le sort ${cardName}!`
        : `Le sort ${cardName} a été lancé!`,
      6000
    );
  }

  /**
   * Notifier la pose d'un piège
   * @param {boolean} byOpponent - true si c'est l'adversaire qui le pose
   */
  trapSet(byOpponent = false) {
    soundManager.play('cardPlace');
    this.notificationService.info(
      byOpponent
        ? 'L\'adversaire pose un piège!'
        : 'Vous posez un piège!',
      5500
    );
  }

  /**
   * Notifier l'activation d'un piège (déclencheur)
   * @param {string} trapName - Nom du piège activé
   * @param {boolean} byOpponent - true si le piège appartient à l'adversaire
   */
  trapActivated(trapName, byOpponent = false) {
    soundManager.play('cardFlip');
    this.notificationService.warning(
      byOpponent
        ? `Piège adverse activé : ${trapName}!`
        : `Votre piège ${trapName} se déclenche!`,
      6500
    );
  }

  /**
   * Notifier la destruction d'une carte
   * @param {string} cardName - Nom de la carte détruite
   * @param {boolean} mine - true si la carte appartient au joueur
   */
  cardDestroyed(cardName, mine = false) {
    soundManager.play('destroy');
    this.notificationService[mine ? 'error' : 'success'](
      mine
        ? `${cardName} est détruit!`
        : `Le monstre adverse ${cardName} est détruit!`,
      6500
    );
  }

  /**
   * Notifier le retrait d'une carte de la zone de combat
   */
  undeclare(cardName, isDefense = false) {
    soundManager.play('cardPlace');
    this.notificationService.info(
      isDefense
        ? `${cardName} ne défend plus.`
        : `${cardName} quitte la zone de combat.`,
      4500
    );
  }

  /**
   * Shi-fu-mi : début, égalité, résultat
   */
  rpsStart() {
    soundManager.play('notification');
    this.notificationService.info('Pierre · Feuille · Ciseaux — le gagnant commence !', 6000);
  }

  rpsTie() {
    soundManager.play('cardFlip');
    this.notificationService.warning('Égalité au shi-fu-mi ! On rejoue.', 6000);
  }

  rpsResult(playerWon) {
    soundManager.play(playerWon ? 'victory' : 'notification');
    this.notificationService[playerWon ? 'success' : 'warning'](
      playerWon
        ? 'Vous remportez le shi-fu-mi — vous commencez !'
        : 'L\'adversaire remporte le shi-fu-mi et commence.',
      6500
    );
  }

  /**
   * Notifier l'activation d'un effet
   * @param {string} cardName - Nom de la carte dont l'effet est activé
   * @param {string} effectDescription - Description de l'effet
   */
  activateEffect(cardName, effectDescription) {
    soundManager.play('cardFlip');
    this.notificationService.info(
      `${cardName} active son effet: ${effectDescription}`,
      6500
    );
  }

  /**
   * Notifier la pioche d'une carte
   * @param {number} count - Nombre de cartes piochées
   */
  drawCard(count = 1) {
    soundManager.play('draw');
    this.notificationService.info(
      `Vous avez pioché ${count} carte${count > 1 ? 's' : ''}`,
      3500
    );
  }

  /**
   * Notifier la victoire
   */
  victory() {
    soundManager.play('victory');
    this.notificationService.success(
      'Vous avez gagné la partie!',
      5000
    );
  }

  /**
   * Notifier la défaite
   */
  defeat() {
    soundManager.play('defeat');
    this.notificationService.error(
      'Vous avez perdu la partie.',
      5000
    );
  }

  /**
   * Notifier qu'un adversaire rejoint la partie
   * @param {string} playerName - Nom du joueur qui rejoint
   */
  playerJoined(playerName) {
    soundManager.play('notification');
    this.notificationService.info(
      `${playerName} a rejoint la partie.`,
      3000
    );
  }

  /**
   * Notifier qu'un adversaire quitte la partie
   * @param {string} playerName - Nom du joueur qui quitte
   */
  playerLeft(playerName) {
    soundManager.play('notification');
    this.notificationService.warning(
      `${playerName} a quitté la partie.`,
      3000
    );
  }

  /**
   * Notifier un événement spécial personnalisé
   * @param {string} message - Message à afficher
   * @param {string} type - Type de notification ('info', 'success', 'warning', 'error')
   * @param {string} sound - Nom du son à jouer (doit être préchargé dans soundManager)
   */
  gameEvent(message, type = 'info', sound = 'notification') {
    soundManager.play(sound);
    this.notificationService[type](message, 4000);
  }
}

// Exporter une instance unique
const gameNotificationService = new GameNotificationService(notificationService);
export default gameNotificationService;